import { useMemo, useState } from "react";
import { ExternalLink, Loader2, Lock, Search } from "lucide-react";
import type { GitHubInstallationSummary } from "@mycompbox/shared";
import {
  useConnectGitHubRepository,
  useCreateGitHubRepository,
  useGitHubRepositories,
} from "@/hooks/queries/use-github";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

function suggestedName(projectName: string): string {
  return projectName.toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") || "new-project";
}

/**
 * Pick one of the CompBox GitHub App's repositories for a project, or create
 * a new one in the company's GitHub organization.
 */
export function GitHubRepoPicker({
  teamId,
  projectName,
  installations,
  onDone,
  onPasteInstead,
}: {
  teamId: string;
  projectName: string;
  installations: GitHubInstallationSummary[];
  onDone: () => void;
  onPasteInstead: () => void;
}) {
  const { data: repos, isLoading, error: listError } = useGitHubRepositories(true);
  const connect = useConnectGitHubRepository(teamId);
  const create = useCreateGitHubRepository(teamId);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<"pick" | "create">("pick");
  const activeInstalls = installations.filter((i) => !i.suspended);
  const orgInstalls = activeInstalls.filter((i) => i.accountType === "Organization");
  const [installationId, setInstallationId] = useState(orgInstalls[0]?.installationId ?? "");
  const [name, setName] = useState(suggestedName(projectName));
  const [isPrivate, setIsPrivate] = useState(true);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (repos ?? []).filter((r) => !q || r.fullName.toLowerCase().includes(q)).slice(0, 50);
  }, [repos, query]);

  async function pick(installation: string, fullName: string) {
    await connect.mutateAsync({ installationId: installation, fullName });
    onDone();
  }

  async function createRepo() {
    await create.mutateAsync({ installationId, name: name.trim(), private: isPrivate });
    onDone();
  }

  return (
    <div className="space-y-3 rounded-lg border border-white/[0.08] p-4">
      <div className="flex gap-2 text-sm">
        <Button size="sm" variant={mode === "pick" ? "default" : "outline"} onClick={() => { setMode("pick"); }}>
          Choose a repository
        </Button>
        <Button
          size="sm"
          variant={mode === "create" ? "default" : "outline"}
          disabled={orgInstalls.length === 0}
          title={orgInstalls.length === 0 ? "GitHub lets apps create repositories only in organizations" : undefined}
          onClick={() => { setMode("create"); }}
        >
          Create a new repository
        </Button>
      </div>

      {mode === "pick" ? (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search repositories"
              value={query}
              onChange={(e) => { setQuery(e.target.value); }}
              aria-label="Search repositories"
            />
          </div>
          {isLoading ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading repositories from GitHub…
            </p>
          ) : listError ? (
            <p className="text-sm text-destructive">{errorText(listError)}</p>
          ) : shown.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No repositories found. On GitHub, add the repository to the CompBox app's list, then search again.
            </p>
          ) : (
            <ul className="max-h-64 divide-y divide-white/[0.06] overflow-y-auto rounded-md border border-white/[0.06]">
              {shown.map((r) => (
                <li key={`${r.installationId}:${r.id}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span className="flex min-w-0 items-center gap-1.5">
                    {r.private ? <Lock className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Private" /> : null}
                    <span className="truncate">{r.fullName}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={connect.isPending}
                    onClick={() => void pick(r.installationId, r.fullName).catch(() => undefined)}
                  >
                    Use this
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {errorText(connect.error) ? <p className="text-sm text-destructive">{errorText(connect.error)}</p> : null}
          {activeInstalls.length > 0 ? (
            <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {activeInstalls.map((i) => (
                <a
                  key={i.installationId}
                  href={i.manageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 underline"
                >
                  {activeInstalls.length > 1 ? `Repo not listed? Add it in ${i.accountLogin}` : "Repo not listed?"}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3">
          {orgInstalls.length > 1 ? (
            <div className="space-y-1">
              <Label htmlFor="new-repo-owner">GitHub organization</Label>
              <select
                id="new-repo-owner"
                className="w-full rounded-md border border-white/[0.08] bg-transparent px-3 py-2 text-sm"
                value={installationId}
                onChange={(e) => { setInstallationId(e.target.value); }}
              >
                {orgInstalls.map((i) => (
                  <option key={i.installationId} value={i.installationId}>
                    {i.accountLogin}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Created in <span className="font-medium text-foreground">{orgInstalls[0]?.accountLogin}</span> on GitHub, with a
              README so it's ready for the first pull request.
            </p>
          )}
          <div className="space-y-1">
            <Label htmlFor="new-repo-name">Repository name</Label>
            <Input id="new-repo-name" value={name} onChange={(e) => { setName(e.target.value); }} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isPrivate} onChange={(e) => { setIsPrivate(e.target.checked); }} />
            Private
          </label>
          {errorText(create.error) ? <p className="text-sm text-destructive">{errorText(create.error)}</p> : null}
          <div className="flex justify-end">
            <Button size="sm" disabled={!name.trim() || !installationId || create.isPending} onClick={() => void createRepo().catch(() => undefined)}>
              {create.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
              Create and connect
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-between gap-2 border-t border-white/[0.06] pt-3">
        <button type="button" className="text-xs text-muted-foreground underline" onClick={onPasteInstead}>
          Not on GitHub? Paste a clone URL
        </button>
        <Button size="sm" variant="outline" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
